import React, { ReactNode } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Autocomplete from 'react-autocomplete';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { IUsers, RootState } from 'store/store';
import {
  createLimitation,
  createLimitationFn,
  removeLimitation,
  removeLimitationFn,
  requireDocument,
  requireDocumentFn,
  updateItem,
  updateItemFn,
} from 'store/providers/providerSlice';
import {
  AutoCompleteWrapperStyle,
  LimitationInputs,
  LimitationTable,
  UserSearchInput,
  UserSearchInputContainer,
  userSearchMenuStyle,
} from './styles';
import messages from './Messages';
import Input from 'components/Input';
import notify from 'components/Notify';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import { MenuItem } from 'components/TaskSearch/styles';
import Select, { IOption } from 'components/Select/Select';
import LimitationRoleRow from './LimitationRoleRow';
import LimitationUserRow from './LimitationUserRow';
import { notifyError } from '../../Notify/Notify';
import Icon from '../../Icon/Icon';
import { black, blue, exactWhite, stringToColour } from 'utils/style/colors';
import Collapsible from '../../Collapsible/Collapsible';
import LimitationButtonGroup from './LimitationButtonGroup';
import Badge from '../../Badge';
import Pill from '../../Pill';
import { appDispatch } from 'store/store';
import {
  ActivePermissions,
  IProviderItemLinkView,
  IProviderItemView,
} from 'store/providers/providerTypes';
import {
  ICompany,
  ICompanyMember,
  ICompanyRole,
} from 'store/companies/companiesTypes';
import {
  IMultipleChoiceQuestionView,
  ISurveyItem,
  ISurveyView,
  SURVEY_ANALYSE_LINK_DESC_NAME,
} from 'store/survey/surveyTypes';
import {
  addRoleToLimitation,
  addRoleToLimitationFn,
  addUserToLimitation,
  addUserToLimitationFn,
  ILimitationStore,
  LimitationType,
  removeRoleFromLimitation,
  removeRoleFromLimitationFn,
  removeUserFromLimitation,
  removeUserFromLimitationFn,
  requireSeveralLimitations,
  requireSeveralLimitationsFn,
  requireSingleLimitation,
  requireSingleLimitationFn,
} from 'store/limitations/limitationsSlice';
import { NumberOrNull } from 'store/entity/types';
import { IUser } from 'store/users/usersType';
import {
  requireCompanyUsers,
  requireCompanyUsersFn,
  searchUser,
  searchUserFn,
} from 'store/users/usersSlice';
import { requireSurvey, requireSurveyFn } from 'store/survey/surveySlice';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  isCompanyAdmin,
  isCompanyOwnerOrSupport,
} from 'store/companies/companiesUtils';
import ButtonGroup from '../../ButtonGroup';
import i18n from '../../../i18n';
import { englishNumber } from '../../../utils/number';

type OwnProps = {
  item: IProviderItemView;
  companyId?: number;
  limitAccessToCompany: boolean;
  companyRoles?: ICompany['roles'];
  companyRolesOptions?: IOption[];
  companyMembers?: ICompany['members'];
  companyMembersOptions?: IOption[];
  analyseItem: IProviderItemView | null;
  analyseSurvey: ISurveyView | null;
};

type StateProps = {
  users: IUsers['map'];
  thisUser: IThisUserState;
  ownerUserId?: number;
  limitations: { [key: number]: ILimitationStore };
  companyName?: string;
  company?: ICompany;
  survey?: ISurveyView;
  analyseItemFound?: IProviderItemLinkView;
  isSupport?: boolean;
  permissionSchemes: ICompany['permissionSchemes'];
};

type DispatchProps = {
  requireMyCompaniesUsers: () => ReturnType<typeof requireCompanyUsersFn>;
  searchUserByPhoneOrEmail: (
    phoneOrEmail: string
  ) => ReturnType<typeof searchUserFn>;
  updateProviderItem: (
    item: IProviderItemView
  ) => ReturnType<typeof updateItemFn>;
  createNewLimitation: (
    item: IProviderItemView,
    type: LimitationType
  ) => ReturnType<typeof createLimitationFn>;
  removeCurrentLimitation: (
    item: IProviderItemView,
    type: LimitationType
  ) => ReturnType<typeof removeLimitationFn>;
  requireLimitation: (
    limitationId: number
  ) => ReturnType<typeof requireSingleLimitationFn>;
  requireMultipleLimitations: (
    limitationIds: number[]
  ) => ReturnType<typeof requireSeveralLimitationsFn>;
  addUserToLimitation: (
    limitationId: number,
    userId: number
  ) => ReturnType<typeof addUserToLimitationFn>;
  removeUserFromLimitation: (
    limitationId: number,
    userId: number
  ) => ReturnType<typeof removeUserFromLimitationFn>;
  addRoleToLimitation: (
    limitationId: number,
    roleId: number
  ) => ReturnType<typeof addRoleToLimitationFn>;
  removeRoleFromLimitation: (
    limitationId: number,
    roleId: number
  ) => ReturnType<typeof removeRoleFromLimitationFn>;
  requireDocument: (id: number) => ReturnType<typeof requireDocumentFn>;
  requireSurvey: (
    surveyItem: ISurveyItem
  ) => ReturnType<typeof requireSurveyFn>;
};

type ComponentProps = OwnProps & StateProps & DispatchProps;

type LimitationStates =
  | 'loadingForEdit'
  | 'loadingForRead'
  | 'loadingForReport'
  | 'loadingForChangeData';

type ItemType = 'mainItem' | 'analyseItem';

type ComponentStates = {
  activeKey: NumberOrNull;
  users: IUser[];
  newUsers: IUser[];
  roles: IOption[];
  newRoles: IOption[];
  searchUser: string;
  loadingForEdit: number[];
  loadingForRead: number[];
  loadingForReport: number[];
  loadingForChangeData: number[];
  preserveParentLimitationLoading: boolean;
  loading: {
    edit: boolean;
    read: boolean;
    report: boolean;
    changeData: boolean;
  };
  filterString?: string;
  initLoaded?: boolean;
  itemType: ItemType;
  analyseItemId: number | null;
  fetchingAnalysedData: boolean;
  // analyseItem: IProviderItemView | null;
  // analyseSurvey: ISurveyView | null;
};

export type ChangeLimitationType = 'role' | 'user';

class LimitationManager extends React.PureComponent<
  ComponentProps,
  ComponentStates
> {
  state: ComponentStates = {
    activeKey: null,
    loading: {
      edit: false,
      read: false,
      changeData: false,
      report: false,
    },
    loadingForEdit: [],
    loadingForRead: [],
    loadingForReport: [],
    loadingForChangeData: [],
    users: [],
    newUsers: [],
    roles: [],
    newRoles: [],
    preserveParentLimitationLoading: false,
    searchUser: '',
    itemType: 'mainItem',
    analyseItemId: null,
    fetchingAnalysedData: false,
    // analyseItem: null,
    // analyseSurvey: null,
  };

  changeLimitationEndpoint: {
    [type in ChangeLimitationType]: {
      add: DispatchProps['addUserToLimitation'];
      remove: DispatchProps['removeUserFromLimitation'];
    };
  } = {
    role: {
      add: this.props.addRoleToLimitation,
      remove: this.props.removeRoleFromLimitation,
    },
    user: {
      add: this.props.addUserToLimitation,
      remove: this.props.removeUserFromLimitation,
    },
  };

  static getDerivedStateFromProps(
    nextProps: ComponentProps,
    prevState: ComponentStates
  ) {
    const { newUsers } = prevState;
    const { companyMembers, limitAccessToCompany } = nextProps;

    const isLimitToCompanyMembers = companyMembers && limitAccessToCompany;

    const users = Object.values(nextProps.users).filter((user) => {
      const userId = user!.id;
      let result;
      if (isLimitToCompanyMembers) {
        const userExistInCompany =
          !!companyMembers![userId] && !companyMembers![userId].inactive;
        result = userExistInCompany
          ? !newUsers.find((selectedUser) => selectedUser.id === userId)
          : false;
        return result;
      } else {
        result = !newUsers.find((selectedUser) => selectedUser.id === userId);
        return result;
      }
    });

    return { users };
  }

  componentDidMount() {
    const {
      item: {
        readLimitationId,
        editLimitationId,
        changeDataLimitationId,
        reportLimitationId,
      },
      requireMyCompaniesUsers,
      requireMultipleLimitations,
    } = this.props;
    this.props.requireDocument(this.props.item.id).then(() => {
      requireMyCompaniesUsers().then(() => {
        let limitList: Array<number | null> = [
          readLimitationId,
          editLimitationId,
          changeDataLimitationId,
          reportLimitationId,
        ];
        limitList = limitList.filter(Number);
        requireMultipleLimitations(limitList as number[]).then(() => {
          this.setState({ initLoaded: true });
        });
      });
    });
    if (this.props.item.type === 'survey' && !this.props.survey) {
      this.props.requireSurvey(this.props.item as ISurveyItem);
    }
  }

  componentDidUpdate(prevProps: ComponentProps) {
    const {
      item: {
        readLimitationId,
        editLimitationId,
        reportLimitationId,
        changeDataLimitationId,
      },
      requireLimitation,
    } = this.props;
    if (
      readLimitationId &&
      readLimitationId !== prevProps.item.readLimitationId
    ) {
      requireLimitation(readLimitationId);
    }
    if (
      editLimitationId &&
      editLimitationId !== prevProps.item.editLimitationId
    ) {
      requireLimitation(editLimitationId);
    }
    if (
      reportLimitationId &&
      reportLimitationId !== prevProps.item.reportLimitationId
    ) {
      requireLimitation(reportLimitationId);
    }
    if (
      changeDataLimitationId &&
      changeDataLimitationId !== prevProps.item.changeDataLimitationId
    ) {
      requireLimitation(changeDataLimitationId);
    }
    if (
      this.state.itemType === 'analyseItem' &&
      this.state.analyseItemId &&
      !this.state.fetchingAnalysedData &&
      !this.props.analyseItem //&&
      // !this.state.analyseItem
    ) {
      this.setState({ fetchingAnalysedData: true });
      this.props.requireDocument(this.state.analyseItemId).then((_res: any) => {
        this.setState({
          fetchingAnalysedData: false,
          // analyseItem: res.item,
        });
      });
    }

    if (
      !this.props.analyseSurvey &&
      this.props.analyseItem //&&
      // !this.state.analyseSurvey
    ) {
      this.props.requireSurvey(
        this.props.analyseItem /*|| this.state.analyseItem*/ as ISurveyItem
      );
      // .then((surveyResponse) => {
      //   if (surveyResponse?.payload?.survey) {
      //     this.setState({ analyseSurvey: surveyResponse.payload.survey });
      //   }
      // });
    }
  }

  handleChangeLimitation = (
    status: boolean,
    limitType: LimitationType
  ): Promise<void> => {
    const { item, createNewLimitation, removeCurrentLimitation } = this.props;
    this.setState({ loading: { ...this.state.loading, [limitType]: true } });
    if (status) {
      return new Promise((resolve, reject) => {
        createNewLimitation(item, limitType)
          .then(() => {
            this.setState({
              loading: { ...this.state.loading, [limitType]: false },
            });
            resolve();
          })
          .catch((err: any) => {
            notifyError(err);
            this.setState({
              loading: { ...this.state.loading, [limitType]: false },
            });
            reject(err);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        removeCurrentLimitation(item, limitType)
          .then(() => {
            this.setState({
              loading: { ...this.state.loading, [limitType]: false },
            });
            resolve();
          })
          .catch((err: any) => {
            notifyError(err);
            this.setState({
              loading: { ...this.state.loading, [limitType]: false },
            });
            reject(err);
          });
      });
    }
  };

  changeRoleUserLoading = (
    limitationType: LimitationType,
    changeType: 'addLoading' | 'removeLoading',
    roleUserId: number
  ) => {
    const capitalizeLimitationType =
      limitationType.charAt(0).toUpperCase() + limitationType.slice(1);
    const loadingLimitationName =
      `loadingFor${capitalizeLimitationType}` as LimitationStates;

    this.setState((prevState) => {
      let limitaiton = cloneDeep(prevState[loadingLimitationName]);
      if (changeType === 'addLoading') {
        limitaiton.push(roleUserId);
      } else if (changeType === 'removeLoading') {
        limitaiton = limitaiton.filter((id) => id !== roleUserId);
      }
      return { ...prevState, [loadingLimitationName]: limitaiton };
    });
  };

  handleChangeCheckbox = (
    limitationType: LimitationType,
    checked: boolean,
    itemType: ChangeLimitationType,
    limitationId: number,
    itemId: number
  ) => {
    const { createNewLimitation, item, analyseItem } = this.props;
    const activeItem = this.state.itemType === 'mainItem' ? item : analyseItem;
    this.changeRoleUserLoading(limitationType, 'addLoading', itemId);
    if (!limitationId && activeItem) {
      createNewLimitation(activeItem, limitationType)
        .then(({ limitationId }) => {
          this.doChangeLimitation(
            limitationType,
            itemId,
            limitationId,
            checked,
            itemType
          );
        })
        .catch((err: any) => {
          notifyError(err);
          this.changeRoleUserLoading(limitationType, 'removeLoading', itemId);
        });
    } else
      this.doChangeLimitation(
        limitationType,
        itemId,
        limitationId,
        checked,
        itemType
      );
  };

  private doChangeLimitation(
    limitationType: 'edit' | 'changeData' | 'report' | 'read',
    userId: number,
    limitationId: number,
    checked: boolean,
    type: ChangeLimitationType
  ) {
    if (checked) {
      this.changeLimitationEndpoint[type]
        .add(limitationId, userId)
        .then(() => {
          this.changeRoleUserLoading(limitationType, 'removeLoading', userId);
        })
        .catch((e) => {
          notifyError(e);
          this.changeRoleUserLoading(limitationType, 'removeLoading', userId);
        });
    } else {
      this.changeLimitationEndpoint[type]
        .remove(limitationId, userId)
        .then(() => {
          this.changeRoleUserLoading(limitationType, 'removeLoading', userId);
        })
        .catch((e) => {
          notifyError(e);
          this.changeRoleUserLoading(limitationType, 'removeLoading', userId);
        });
    }
  }

  addNewUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!this.props.limitAccessToCompany) {
      this.props
        .searchUserByPhoneOrEmail(englishNumber(this.state.searchUser))
        .then(
          ({ users }) => {
            if (users.length < 1) {
              notify(
                i18n.t('userNotFound', { ns: 'documentManager' }),
                'error'
              );
            } else {
              this.setState((prevState) => {
                const newUsers = [...prevState.newUsers];
                users.forEach((user: any) => {
                  if (!prevState.newUsers.find((item) => item.id === user.id)) {
                    newUsers.unshift(user);
                  }
                });
                return { newUsers };
              });
            }
          },
          (err: any) => {
            display(err);
          }
        );
    }
  };

  handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchUser: e.target.value });
  };

  handleSelectUser = (_value: string, user: IUser) => {
    this.setState((prevState) => {
      const newUsers = [...prevState.newUsers];
      if (!prevState.newUsers.find((item) => item.id === user.id)) {
        newUsers.unshift(user);
      }
      return { newUsers };
    });
  };

  handleSelectRole = (newRoles: IOption[]) => {
    this.setState({ newRoles });
  };

  renderAutocompleteMenu = (
    children: ReactNode,
    value: string
  ): React.ReactNode => {
    return (
      <ul key={value} style={userSearchMenuStyle}>
        {children}
      </ul>
    );
  };

  renderAutocompleteItem = (item: IUser, isHighlighted: boolean) => {
    return (
      <MenuItem key={`${item.id}_${item.name}`} isHighlighted={isHighlighted}>
        {item.name}
      </MenuItem>
    );
  };

  phoneNumberInput = (props: any) => {
    const { ref, ...restProps } = props;
    return <UserSearchInput ref={ref} {...restProps} />;
  };

  handleChangePreserveParentEdit = () => {
    this.setState({ preserveParentLimitationLoading: true });
    const clonedItem = cloneDeep(
      this.state.itemType === 'mainItem'
        ? this.props.item
        : this.props.analyseItem //|| this.state.analyseItem
    );
    if (clonedItem) {
      clonedItem.preserveParentEditLimitation =
        !clonedItem.preserveParentEditLimitation;
      this.props
        .updateProviderItem(clonedItem)
        .then(() => {
          this.setState({ preserveParentLimitationLoading: false });
        })
        .catch((err: any) => {
          this.setState({ preserveParentLimitationLoading: false });
          display(err);
        });
    }
  };

  handleChangeActiveKey = (
    event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>
  ) => {
    const {
      currentTarget: { dataset },
    } = event;
    const roleId = Number(dataset.roleId);
    this.setState((prevState) => ({
      activeKey: prevState.activeKey === roleId ? null : roleId,
    }));
  };

  render() {
    const {
      item,
      users,
      thisUser,
      ownerUserId,
      limitations,
      companyName,
      limitAccessToCompany,
      company,
      survey,
      permissionSchemes,
      companyRoles,
      analyseSurvey,
      analyseItem,
    } = this.props;

    const activeItem = this.state.itemType === 'mainItem' ? item : analyseItem; //|| this.state.analyseItem;

    const activeSurvey =
      this.state.itemType === 'mainItem' ? survey : analyseSurvey; //|| this.state.analyseSurvey;

    const reportPermissionSchemesActive = new Set<string>();
    const fillPermissionSchemesActive = new Set<string>();
    let fillTextOverwrite: string | undefined = undefined;
    if (activeSurvey) {
      if (activeSurvey.visibilityStrategy === 'protected') {
        if (activeSurvey.showResponseToTarget) {
          fillTextOverwrite = i18n.t('fillProtectedTarget_ShowToTarget', {
            ns: 'documentManager',
          });
        } else {
          fillTextOverwrite = i18n.t('invalidFill', { ns: 'documentManager' });
        }
      } else if (activeSurvey.visibilityStrategy === 'personal') {
        if (activeSurvey.showResponseToTarget) {
          fillTextOverwrite = i18n.t('fillPersonalTarget_ShowToTarget', {
            ns: 'documentManager',
          });
        } else {
          fillTextOverwrite = i18n.t('fillPersonalTarget', {
            ns: 'documentManager',
          });
        }
      } else {
        fillTextOverwrite = i18n.t('fillPublic', { ns: 'documentManager' });
      }
      Object.values(activeSurvey.questionMap || {})
        .filter((q) => !q.data.deleted && q.data.type === 'multipleChoice')
        .forEach((q) => {
          const qObj = q.data as IMultipleChoiceQuestionView;
          if (qObj.fillPermissionSchemeList)
            qObj.fillPermissionSchemeList.forEach((it) => {
              if (permissionSchemes[it]?.properties.name.endsWith('*'))
                fillPermissionSchemesActive.add(
                  permissionSchemes[it]?.properties.name.substr(
                    0,
                    permissionSchemes[it]?.properties.name.length - 1
                  )
                );
              Object.keys(
                permissionSchemes[it]?.properties?.roleIdTagIdListMap || {}
              ).forEach((roleId) => {
                fillPermissionSchemesActive.add(
                  companyRoles?.[Number(roleId)]?.name ?? ''
                );
              });
            });
          if (qObj.reportPermissionSchemeList)
            qObj.reportPermissionSchemeList.forEach((it) => {
              if (permissionSchemes[it]?.properties.name.endsWith('*'))
                reportPermissionSchemesActive.add(
                  permissionSchemes[it]?.properties.name.substr(
                    0,
                    permissionSchemes[it]?.properties.name.length - 1
                  )
                );
              Object.keys(
                permissionSchemes[it]?.properties?.roleIdTagIdListMap || {}
              ).forEach((roleId) => {
                reportPermissionSchemesActive.add(
                  companyRoles?.[Number(roleId)]?.name ?? ''
                );
              });
            });
        });
    }
    const readLimitation = activeItem?.readLimitationId
      ? limitations[activeItem?.readLimitationId]?.data
      : undefined;
    const editLimitation = activeItem?.editLimitationId
      ? limitations[activeItem?.editLimitationId]?.data
      : undefined;
    const changeDataLimitation = activeItem?.changeDataLimitationId
      ? limitations[activeItem?.changeDataLimitationId]?.data
      : undefined;
    const reportLimitation = activeItem?.reportLimitationId
      ? limitations[activeItem?.reportLimitationId]?.data
      : undefined;

    if (!this.state.initLoaded) {
      return (
        <div className="progress">
          <div className="indeterminate" />
        </div>
      );
    }

    const {
      loadingForChangeData,
      loadingForEdit,
      loadingForRead,
      loadingForReport,
      newRoles,
      newUsers,
      preserveParentLimitationLoading,
      filterString,
      itemType,
    } = this.state;

    let usersList: number[] = [];
    let rolesList: number[] = [];
    if (readLimitation) {
      usersList = [...usersList, ...(readLimitation.exceptedUsers || [])];
      rolesList = [...rolesList, ...(readLimitation.exceptedRoles || [])];
    }
    if (editLimitation) {
      usersList = [...usersList, ...(editLimitation.exceptedUsers || [])];
      rolesList = [...rolesList, ...(editLimitation.exceptedRoles || [])];
    }
    if (reportLimitation) {
      usersList = [...usersList, ...(reportLimitation.exceptedUsers || [])];
      rolesList = [...rolesList, ...(reportLimitation.exceptedRoles || [])];
    }
    if (changeDataLimitation) {
      usersList = [...usersList, ...(changeDataLimitation.exceptedUsers || [])];
      rolesList = [...rolesList, ...(changeDataLimitation.exceptedRoles || [])];
    }
    const finalParentEffectivePermission: ActivePermissions = !activeItem?.dadId
      ? {
          effectiveReadPermission: [
            limitAccessToCompany ? 'LIMITED_TO_COMPANY_MEMBERS' : 'PUBLIC',
          ],
        }
      : activeItem?.preserveParentEditLimitation
        ? activeItem?.parentActivePermissions || {}
        : {
            effectiveReadPermission:
              activeItem?.parentActivePermissions?.effectiveReadPermission?.filter(
                (it) => !it.startsWith('C') && !it.startsWith('U')
              ),
          };
    if (finalParentEffectivePermission?.effectiveEditPermission) {
      usersList = [
        ...usersList,
        ...(finalParentEffectivePermission?.effectiveEditPermission
          ?.filter((it) => it.startsWith('U'))
          .map((it) => Number(it.substr(1))) || []),
      ];
      rolesList = [
        ...rolesList,
        ...(finalParentEffectivePermission?.effectiveEditPermission
          ?.filter((it) => it.startsWith('R'))
          .map((it) => Number(it.substr(1))) || []),
      ];
    }
    if (finalParentEffectivePermission?.effectiveChangeDataPermission) {
      usersList = [
        ...usersList,
        ...(finalParentEffectivePermission?.effectiveChangeDataPermission
          ?.filter((it) => it.startsWith('U'))
          .map((it) => Number(it.substr(1))) || []),
      ];
      rolesList = [
        ...rolesList,
        ...(finalParentEffectivePermission?.effectiveChangeDataPermission
          ?.filter((it) => it.startsWith('R'))
          .map((it) => Number(it.substr(1))) || []),
      ];
    }
    if (finalParentEffectivePermission?.effectiveReportPermission) {
      usersList = [
        ...usersList,
        ...(finalParentEffectivePermission?.effectiveReportPermission
          ?.filter((it) => it.startsWith('U'))
          .map((it) => Number(it.substr(1))) || []),
      ];
      rolesList = [
        ...rolesList,
        ...(finalParentEffectivePermission?.effectiveReportPermission
          ?.filter((it) => it.startsWith('R'))
          .map((it) => Number(it.substr(1))) || []),
      ];
    }
    if (finalParentEffectivePermission?.effectiveReadPermission) {
      usersList = [
        ...usersList,
        ...(finalParentEffectivePermission?.effectiveReadPermission
          ?.filter((it) => it.startsWith('U'))
          .map((it) => Number(it.substr(1))) || []),
      ];
      rolesList = [
        ...rolesList,
        ...(finalParentEffectivePermission?.effectiveReadPermission
          ?.filter((it) => it.startsWith('R'))
          .map((it) => Number(it.substr(1))) || []),
      ];
    }

    usersList = Array.from(new Set(usersList));
    rolesList = Array.from(new Set(rolesList));

    const isOwner =
      ownerUserId === Number(thisUser.userId) ||
      isCompanyAdmin(thisUser, company!);

    const haveEditPermission = activeItem?.editable;
    const parentPublicAccessable =
      finalParentEffectivePermission?.effectiveReadPermission?.includes(
        'PUBLIC'
      );
    const publicAccessible = parentPublicAccessable && !readLimitation;
    const parentCompanyAccessable =
      finalParentEffectivePermission?.effectiveReadPermission?.includes(
        'LIMITED_TO_COMPANY_MEMBERS'
      );
    const companyAccessible = parentCompanyAccessable && !readLimitation;
    const isDisabled = isOwner ? false : !haveEditPermission;
    const pathViewPermissoins = (
      activeItem?.activePermissions?.effectivePathViewPermission || []
    ).filter((it) => {
      if (
        activeItem?.activePermissions?.effectiveReadPermission?.includes(it) ||
        activeItem?.activePermissions?.effectiveChangeDataPermission?.includes(
          it
        ) ||
        activeItem?.activePermissions?.effectiveReportPermission?.includes(
          it
        ) ||
        activeItem?.activePermissions?.effectiveEditPermission?.includes(it)
      )
        return false;
      return true;
    });

    // check if analysed exists

    let questionTypeAccess: { [key: string]: string } = {
      mainItem: i18n.t('indicatorItem', { ns: 'documentManager' }),
    };

    if (this.props.analyseItemFound) {
      questionTypeAccess = {
        ...questionTypeAccess,
        analyseItem: i18n.t('analyseItem', { ns: 'documentManager' }),
      };
    }

    const handleChangeQuestionType = (value: string) => {
      this.setState({
        itemType: value as ItemType,
        roles: [],
        newRoles: [],
      });
    };

    const showReportLimitation =
      activeItem?.type === 'folder' ||
      activeItem?.type === 'survey' ||
      activeItem?.type === 'surveyReport' ||
      activeItem?.type === 'searchTag';
    const showReadLimitation =
      activeItem?.type !== 'surveyReport' &&
      !(publicAccessible || companyAccessible) &&
      fillTextOverwrite !== i18n.t('invalidFill', { ns: 'documentManager' });
    const showChangeDataLimitation = true;
    const showEditLimitation = true;
    return (
      <div>
        {Object.keys(questionTypeAccess).length > 1 && (
          <div className="w-full my-4">
            <ButtonGroup
              items={questionTypeAccess}
              onSelect={handleChangeQuestionType}
              selected={itemType}
            />
          </div>
        )}
        {activeItem?.dadId !== null && isOwner && (
          <Input
            type="checkbox"
            checked={activeItem?.preserveParentEditLimitation}
            onChange={this.handleChangePreserveParentEdit}
            id={`preserveParentEditLimitation_${itemType}`}
            label={
              i18n.t('preserveParentEditLimitation', {
                ns: 'documentManager',
              }) as string
            }
            loading={preserveParentLimitationLoading}
            wrapperStyle={{ marginBottom: '10px' }}
          />
        )}
        {
          <React.Fragment>
            <form onSubmit={this.addNewUser}>
              <Collapsible
                defaultOpen={false}
                header={
                  i18n.t('addRoleOrUser', { ns: 'documentManager' }) as string
                }
                iconName={'plus-square'}
                style={{ margin: '0', overflow: 'visible' }}
                showOverflow={true}
              >
                <LimitationInputs>
                  {this.props.companyRolesOptions && (
                    <Select
                      options={this.props.companyRolesOptions.filter(
                        (r) => !rolesList.includes(r.value as number)
                      )}
                      label={
                        i18n.t('givePermissionToRoles', {
                          ns: 'documentManager',
                        }) as string
                      }
                      placeholder={
                        i18n.t('selectACompanyRole', {
                          ns: 'documentManager',
                        }) as string
                      }
                      values={newRoles}
                      showValues={false}
                      multiChoice={true}
                      onChangeValues={this.handleSelectRole}
                    />
                  )}
                  {
                    <UserSearchInputContainer
                      withoutButton={limitAccessToCompany}
                    >
                      {/*prevent setting user based access*/}
                      {
                        <label htmlFor="searchUser">
                          {
                            i18n.t('givePermissionToUsers', {
                              ns: 'documentManager',
                            }) as string
                          }
                        </label>
                      }
                      {
                        <Autocomplete
                          inputProps={{
                            id: 'searchUser',
                            placeholder:
                              limitAccessToCompany && companyName
                                ? messages.searchInCompany(companyName)
                                : i18n.t('enterPhoneNumberOrName', {
                                    ns: 'documentManager',
                                  }),
                          }}
                          value={this.state.searchUser}
                          items={this.state.users.filter(
                            (u) => !usersList.includes(u.id)
                          )}
                          getItemValue={(i) => i.name}
                          wrapperStyle={AutoCompleteWrapperStyle}
                          onChange={this.handleSearchInput}
                          renderInput={this.phoneNumberInput}
                          onSelect={this.handleSelectUser}
                          renderMenu={this.renderAutocompleteMenu}
                          renderItem={this.renderAutocompleteItem}
                          shouldItemRender={(i, value) =>
                            i.name.indexOf(value) > -1
                          }
                        />
                      }
                      {!limitAccessToCompany && (
                        <Button
                          style={{ marginInlineStart: '1rem' }}
                          type="submit"
                          color="green"
                        >
                          {
                            i18n.t('searchUser', {
                              ns: 'documentManager',
                            }) as string
                          }
                        </Button>
                      )}
                    </UserSearchInputContainer>
                  }
                </LimitationInputs>
              </Collapsible>
            </form>
            <div
              style={{
                padding: '10px 0px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5>
                {
                  i18n.t('activePermissions', {
                    ns: 'documentManager',
                  }) as string
                }
              </h5>
              <Input
                id="filterInput"
                type={'text'}
                style={{
                  height: '2rem',
                }}
                placeholder={i18n.t('searchUser', {
                  ns: 'documentManager',
                })}
                value={filterString}
                onChange={(e) =>
                  this.setState({ filterString: e.target.value })
                }
              />
            </div>
            <LimitationTable>
              <div
                className="px-4 py-2"
                style={{
                  background: black,
                  color: exactWhite,
                  borderRadius: '0.5rem',
                  fontWeight: 'bold',
                }}
              >
                {
                  i18n.t('name', {
                    ns: 'documentManager',
                  }) as string
                }
              </div>
              <div
                className="px-4 py-2"
                style={{
                  background: black,
                  color: exactWhite,
                  borderRadius: '0.5rem',
                  fontWeight: 'bold',
                }}
              >
                {
                  i18n.t('limitation', {
                    ns: 'documentManager',
                  }) as string
                }
              </div>
              <div>
                <>
                  <Icon
                    type={publicAccessible ? 'globe' : 'briefcase'}
                    color={publicAccessible ? blue : black}
                    style={{ marginInlineStart: '27px' }}
                  />{' '}
                  {publicAccessible
                    ? i18n.t('publicAccess', {
                        ns: 'documentManager',
                      })
                    : messages.allMembers(companyName)}
                </>
              </div>
              <div>
                <LimitationButtonGroup
                  style={{ maxWidth: '25%', flex: '1' }}
                  items={[
                    {
                      preventFromParent: !(
                        parentCompanyAccessable || parentPublicAccessable
                      ),
                      fromParent: false,
                      selected:
                        !readLimitation &&
                        !!(parentCompanyAccessable || parentPublicAccessable),
                      type: 'read',
                      disabled: !(
                        parentCompanyAccessable || parentPublicAccessable
                      ),
                    },
                  ]}
                  onSelect={(item) => {
                    if (
                      !(parentCompanyAccessable || parentPublicAccessable) &&
                      item
                    ) {
                      notifyError(
                        i18n.t('permissionIsFromParent', {
                          ns: 'documentManager',
                          companyName,
                        })
                      );
                    } else this.handleChangeLimitation(!item, 'read');
                  }}
                />
              </div>
              {this.props.companyRoles &&
                Object.keys(this.props.companyRoles).length > 0 &&
                newRoles.map((role, idx: number) => {
                  const companyRole = Object.values(
                    this.props.companyRoles!
                  ).find((r) => r.id === role.value) as ICompanyRole;

                  const isActive = this.state.activeKey === role.value;
                  if (!role) {
                    return <div key={idx}></div>;
                  }
                  return (
                    <LimitationRoleRow
                      key={`new_role_${companyRole.id}_${itemType}`}
                      isActive={isActive}
                      isOwner={isOwner}
                      isDisabled={isDisabled}
                      onChangeActiveRole={this.handleChangeActiveKey}
                      onChangeLimitation={this.handleChangeCheckbox}
                      role={companyRole}
                      users={users}
                      readLimitation={readLimitation!}
                      reportLimitation={reportLimitation!}
                      changeDataLimitation={changeDataLimitation!}
                      editLimitation={editLimitation!}
                      loadingForRead={loadingForRead.some(
                        (id) => id === companyRole.id
                      )}
                      loadingForReport={loadingForReport.some(
                        (id) => id === companyRole.id
                      )}
                      loadingForChangeData={loadingForChangeData.some(
                        (id) => id === companyRole.id
                      )}
                      loadingForEdit={loadingForEdit.some(
                        (id) => id === companyRole.id
                      )}
                      parentActivePermission={finalParentEffectivePermission}
                      filterString={filterString}
                      showEditLimitation={showEditLimitation}
                      showReportLimitation={showReportLimitation}
                      showChangeDataLimitation={showChangeDataLimitation}
                      showReadLimitation={showReadLimitation}
                      fillTextOverwrite={fillTextOverwrite}
                      fillPermissionSchemeActive={fillPermissionSchemesActive.has(
                        companyRole.name
                      )}
                      reportPermissionSchemeActive={reportPermissionSchemesActive.has(
                        companyRole.name
                      )}
                      item={activeItem}
                      survey={activeSurvey}
                      permissionScheme={permissionSchemes}
                    />
                  );
                })}
              {this.props.companyRoles &&
                Object.keys(this.props.companyRoles).length > 0 &&
                rolesList.map((roleId, idx: number) => {
                  const role = Object.values(this.props.companyRoles!).find(
                    (role) => role.id === roleId
                  ) as ICompanyRole;

                  if (newRoles.find((u) => u.value === roleId)) {
                    return null;
                  }

                  const isActive = this.state.activeKey === roleId;
                  if (!role) {
                    return <div key={idx}></div>;
                  }
                  if (
                    filterString &&
                    !role.name.includes(filterString) &&
                    !role.users.find((a: any) =>
                      users[a.userId].name.includes(filterString)
                    )
                  )
                    return null;
                  return (
                    <LimitationRoleRow
                      key={`role_${roleId}_${itemType}`}
                      isActive={isActive || !!filterString}
                      isDisabled={isDisabled}
                      isOwner={isOwner}
                      onChangeActiveRole={this.handleChangeActiveKey}
                      onChangeLimitation={this.handleChangeCheckbox}
                      role={role}
                      users={users}
                      readLimitation={readLimitation!}
                      reportLimitation={reportLimitation!}
                      changeDataLimitation={changeDataLimitation!}
                      editLimitation={editLimitation!}
                      loadingForRead={loadingForRead.some(
                        (id) => id === role.id
                      )}
                      loadingForReport={loadingForReport.some(
                        (id) => id === role.id
                      )}
                      loadingForChangeData={loadingForChangeData.some(
                        (id) => id === role.id
                      )}
                      loadingForEdit={loadingForEdit.some(
                        (id) => id === role.id
                      )}
                      parentActivePermission={finalParentEffectivePermission}
                      filterString={filterString}
                      showEditLimitation={showEditLimitation}
                      showReportLimitation={showReportLimitation}
                      showChangeDataLimitation={showChangeDataLimitation}
                      showReadLimitation={showReadLimitation}
                      fillTextOverwrite={fillTextOverwrite}
                      fillPermissionSchemeActive={fillPermissionSchemesActive.has(
                        role.name
                      )}
                      reportPermissionSchemeActive={reportPermissionSchemesActive.has(
                        role.name
                      )}
                      item={activeItem}
                      survey={activeSurvey}
                      permissionScheme={permissionSchemes}
                    />
                  );
                })}
              {newUsers.map((user) => {
                return (
                  <LimitationUserRow
                    key={`new_user_${user.id}`}
                    isDisabled={isDisabled}
                    isOwner={isOwner}
                    onChangeLimitation={this.handleChangeCheckbox}
                    user={user}
                    readLimitation={readLimitation!}
                    reportLimitation={reportLimitation!}
                    changeDataLimitation={changeDataLimitation!}
                    editLimitation={editLimitation!}
                    loadingForRead={loadingForRead.some((id) => id === user.id)}
                    loadingForReport={loadingForReport.some(
                      (id) => id === user.id
                    )}
                    loadingForChangeData={loadingForChangeData.some(
                      (id) => id === user.id
                    )}
                    loadingForEdit={loadingForEdit.some((id) => id === user.id)}
                    parentActivePermission={finalParentEffectivePermission}
                    filterString={filterString}
                    showEditLimitation={showEditLimitation}
                    showReportLimitation={showReportLimitation}
                    showChangeDataLimitation={showChangeDataLimitation}
                    showReadLimitation={showReadLimitation}
                    fillTextOverwrite={fillTextOverwrite}
                    companyMember={this.props.companyMembers![user.id]!}
                  />
                );
              })}
              {usersList.map((userId) => {
                const user: IUser = (users as any)[userId];
                if (!user || newUsers.find((u) => u.id === userId)) {
                  return null;
                }
                const companyMember: ICompanyMember =
                  this.props.companyMembers![user.id]!;
                if (
                  !filterString ||
                  user.name.includes(filterString) ||
                  Object.values(companyMember?.roles).find((it) =>
                    it.name.includes(filterString)
                  )
                ) {
                  return (
                    <LimitationUserRow
                      key={`new_user_${user.id}`}
                      isDisabled={isDisabled}
                      isOwner={isOwner}
                      onChangeLimitation={this.handleChangeCheckbox}
                      user={user}
                      readLimitation={readLimitation!}
                      reportLimitation={reportLimitation!}
                      changeDataLimitation={changeDataLimitation!}
                      editLimitation={editLimitation!}
                      loadingForRead={loadingForRead.some(
                        (id) => id === user.id
                      )}
                      loadingForReport={loadingForReport.some(
                        (id) => id === user.id
                      )}
                      loadingForChangeData={loadingForChangeData.some(
                        (id) => id === user.id
                      )}
                      loadingForEdit={loadingForEdit.some(
                        (id) => id === user.id
                      )}
                      parentActivePermission={finalParentEffectivePermission}
                      filterString={filterString}
                      showEditLimitation={showEditLimitation}
                      showReportLimitation={showReportLimitation}
                      showChangeDataLimitation={showChangeDataLimitation}
                      showReadLimitation={showReadLimitation}
                      fillTextOverwrite={fillTextOverwrite}
                      companyMember={companyMember}
                    />
                  );
                }
                return null;
              })}
            </LimitationTable>
          </React.Fragment>
        }
        {item.type === 'folder' && (
          <Collapsible
            defaultOpen={false}
            header={messages.otherPathView(pathViewPermissoins.length)}
          >
            <div
              style={{
                overflow: 'scroll',
                gap: '5px',
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {pathViewPermissoins.sort().map((it, idx) => {
                if (it.startsWith('R'))
                  return (
                    <Badge key={idx} color={stringToColour(it.substr(1))}>
                      {company?.roles?.[Number(it.substr(1))]?.name || it}
                    </Badge>
                  );
                if (it.startsWith('U'))
                  return (
                    <Pill key={idx}>
                      {users[Number(it.substr(1))]?.name || it}
                    </Pill>
                  );
                return <></>;
              })}
            </div>
          </Collapsible>
        )}
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (
  state,
  props
) => {
  const limitations = state.limitations.map;
  const users = state.users.map;
  const thisUser = state.thisUser;
  const ownerUserId = state.providers.map[props.item.providerId]?.data?.user;
  // let companyMembers;
  let companyName;
  let company;
  let analyseItem;
  let analyseSurvey;

  let permissionSchemes: ICompany['permissionSchemes'] = {};
  if (Object.keys(users).length > 0 && props.companyId) {
    company = state.companies.data[props.companyId]!;
    permissionSchemes = company.permissionSchemes;
    companyName = company.name;
    // companyMembers = Object.values(company.members || {}).filter(
    //   (v) => !v.inactive
    // );
  }
  const analyseItemFound: IProviderItemLinkView | undefined =
    props.item.itemLinks.find(
      (item) => item.description === SURVEY_ANALYSE_LINK_DESC_NAME
    );

  for (const [key, value] of Object.entries(state.providers.items)) {
    if (+key === props.item.providerId) {
      analyseItem = Object.values(value.items).find(
        (item) => item.data.id === analyseItemFound?.destinationItemId
      )?.data;
      const surveyId = analyseItem?.surveyId;
      analyseSurvey = Object.values(state.survey?.data)?.find(
        (survey) => survey?.data?.id === surveyId
      )?.data;
      break;
    }
  }

  let survey: ISurveyView | undefined = undefined;
  if (props.item.type === 'survey')
    survey = state.survey?.data?.[(props.item as ISurveyItem).surveyId]?.data;
  const isSupport = company && isCompanyOwnerOrSupport(thisUser, company);
  return {
    survey,
    limitations,
    users,
    thisUser,
    companyName,
    // companyMembers,
    company,
    ownerUserId,
    permissionSchemes,
    analyseItem,
    analyseSurvey,
    analyseItemFound,
    isSupport,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = () => {
  return {
    requireMyCompaniesUsers: () => appDispatch(requireCompanyUsers({})),
    searchUserByPhoneOrEmail: (phoneOrEmail: string) =>
      appDispatch(searchUser({ phoneOrEmail })),
    updateProviderItem: (item: IProviderItemView) =>
      appDispatch(updateItem({ item })),
    createNewLimitation: (item: IProviderItemView, type: LimitationType) =>
      appDispatch(createLimitation({ item, type })),
    removeCurrentLimitation: (item: IProviderItemView, type: LimitationType) =>
      appDispatch(removeLimitation({ item, type })),
    requireLimitation: (limitationId: number) =>
      appDispatch(requireSingleLimitation({ id: limitationId })),
    requireMultipleLimitations: (limitationList: number[]) =>
      appDispatch(requireSeveralLimitations({ limitList: limitationList })),
    addUserToLimitation: (limitationId: number, userId: number) =>
      appDispatch(addUserToLimitation({ limitationId, userId })),
    removeUserFromLimitation: (limitationId: number, userId: number) =>
      appDispatch(removeUserFromLimitation({ limitationId, userId })),
    addRoleToLimitation: (limitationId: number, roleId: number) =>
      appDispatch(addRoleToLimitation({ limitationId, roleId })),
    removeRoleFromLimitation: (limitationId: number, roleId: number) =>
      appDispatch(removeRoleFromLimitation({ limitationId, roleId })),
    requireDocument: (id: number) =>
      appDispatch(requireDocument({ documentId: id })),
    requireSurvey: (surveyItem: ISurveyItem) =>
      appDispatch(requireSurvey({ providerSurveyItemData: surveyItem })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LimitationManager);
